import { i18n } from '@/i18n';
import { SharedRoutes } from '@/shared-routes';
import { backOfficeRoutes } from '@/views/gallery/back-office-routes';
import type { RouteLocation, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import { publicRoutes } from './public-routes';
import { AuthGuard, RedirectAuthenticatedUser } from '@/auth/auth-guards';
import { trackNavigation } from './composables/useSafeRouterBack';

const { t } = i18n.global;

const determineRoutes = (): Array<RouteRecordRaw> => {
  return [
    {
      path: SharedRoutes.LOGOUT,
      component: () => import('./views/logout/Logout.vue'),
    },
    {
      path: SharedRoutes.LOGIN,
      component: () => {
        return import('./views/login/Login.vue');
      },
      beforeEnter: () => RedirectAuthenticatedUser(),
    },
    {
      path: SharedRoutes.RESET_PASSWORD,
      component: () => import('./views/reset-password/ResetPassword.vue'),
      beforeEnter: () => RedirectAuthenticatedUser(),
    },
    ...backOfficeRoutes,
    ...publicRoutes,
    {
      meta: { title: t('platform.app.error.403.title') },
      path: SharedRoutes.UNVERIFIED,
      component: () => import(`./views/unauthorized/Unauthorized.vue`),
    },
    {
      meta: { title: t('platform.app.error.403.title') },
      path: SharedRoutes.UNAUTHORIZED,
      component: () => import(`./views/unauthorized/Unauthorized.vue`),
    },
    {
      name: 'NotFound',
      meta: { title: t('platform.app.error.404.title') },
      path: '/:catchAll(.*)*',
      component: () => import(`./views/not-found/NotFound.vue`),
      beforeEnter: (to: RouteLocation | RouteLocationNormalized) => AuthGuard(to),
    },
  ];
};

const routes = determineRoutes();

const router = createRouter({
  history: createWebHistory(),
  routes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scrollBehavior: (to: RouteLocationNormalized, from: RouteLocationNormalized, savedPosition: any | null) => {
    const transitionTime = 225; // neon-fade-transition time
    const scrollTo = savedPosition || { left: 0, top: 0 };

    // TODO: is there a more generic solution for scroll preservation need check on per route basis?
    const isNavigatingBetweenActivationMenuTabs = from?.params?.section === 'listings' && to?.params?.section === 'listings';
    const isNavigatingInsideArtistProfile = from?.meta?.isArtistProfileRoute && to?.meta?.isArtistProfileRoute;

    if (
      (isNavigatingBetweenActivationMenuTabs && from?.meta?.preserveScrollPosition)
      || isNavigatingInsideArtistProfile
    ) {
      scrollTo.left = window.scrollX;
      scrollTo.top = window.scrollY;
    }

    setTimeout(() => {
      window.scrollTo(scrollTo.left, scrollTo.top);
    }, transitionTime);
  },
});

router.beforeEach((to: RouteLocation) => {
  trackNavigation({
    path: to.path,
    fullPath: to.fullPath,
    routerPath: to.matched[to.matched.length - 1]?.path,
  });
});

export default router;
